import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { useTranslation } from 'react-i18next';
import { AppDispatch } from '../store';
import { fetchColorCatalog } from '../store/colorsSlice';
import { WorldCanvas, WorldCanvasHandle, Viewport } from '../components/WorldCanvas/WorldCanvas';
import { GridOverlay } from '../components/GridOverlay/GridOverlay';
import { ZoneOverlay } from '../components/ZoneOverlay/ZoneOverlay';
import { WebSocketClient, WsEvent, WsStatusInfo, getWsBase } from '../api/websocket';
import { ConnectionDot } from '../components/ConnectionDot/ConnectionDot';
import { ColorPickerModal } from '../components/ColorPickerModal';
import { Point, samePoint } from '../types/point';
import { Zone } from '../types/zone';
import { useAuth } from '../hooks/useAuth';
import { createPoint as createPointApi, updatePoint as updatePointApi, getUserPoints, deletePoint as deletePointApi } from '../api/points';
import { getZonesInArea, getZonePrice, createZone, deleteZone, getMyZones, ZonePriceResult } from '../api/zones';
import { isZoneActive } from '../types/zone';
import { ZoneDrawOverlay } from '../components/ZoneOverlay/ZoneDrawOverlay';
import { getMe } from '../api/auth';
import { toastError, toastInfo, toastSuccess } from '../utils/toast';
import { useHoldRepeat } from '../hooks/useHoldRepeat';
import { useSpeechBubbles } from '../hooks/useSpeechBubbles';
import { useMoveAggregator } from '../hooks/useAnalytics';
import { trackEvent } from '../utils/analytics';
import { SpeechBubbles } from '../components/SpeechBubbles';
import { AppNav } from '../components/AppNav/AppNav';
import {
  DEFAULT_SPEECH_STYLE,
  isPremiumSpeechStyle,
  SPEECH_STYLE_OPTIONS,
  SpeechBubbleStyle,
} from '../utils/speechStyles';
import '../styles/dashboard.scss';

const MOVE_DIRS: Record<string, [number, number]> = { up: [0,1], down: [0,-1], left: [-1,0], right: [1,0] };

// Mobile control settings, switched live via class modifiers on `.game-page`
// and persisted in localStorage. Three orthogonal axes:
//   layout (onehand/split/taps) · hand (right/left) · d-pad size (compact/large)
// Defaults (onehand · right · compact) reproduce the original mobile layout.
// `taps`: no d-pad — a quick tap (not a drag, so panning still works) in the
// left/right/top/bottom region of the field steps that way.
type ControlLayout = 'onehand' | 'split' | 'taps';
type ControlHand   = 'right' | 'left';
type DpadSize      = 'compact' | 'large';
const CONTROL_LAYOUTS: ControlLayout[] = ['onehand', 'split', 'taps'];
const CONTROL_HANDS:   ControlHand[]   = ['right', 'left'];
const DPAD_SIZES:      DpadSize[]      = ['compact', 'large'];
const GRID_BRIGHTNESS_LEVELS = [1, 2, 3, 4];
const readControlLayout = (): ControlLayout => {
  const s = localStorage.getItem('gameControls');
  if (s === 'split' || s === 'taps') return s;
  if (s === 'swipe') return 'taps'; // migrate the removed swipe preset
  return 'onehand';
};
const readControlHand = (): ControlHand =>
  localStorage.getItem('gameControlsHand') === 'left' ? 'left' : 'right';
const readDpadSize = (): DpadSize =>
  localStorage.getItem('gameControlsSize') === 'large' ? 'large' : 'compact';

export const GamePage: React.FC = () => {
  const { t } = useTranslation();
  const { pointId } = useParams<{ pointId: string }>();
  const moveTracker = useMoveAggregator({ pointId });
  const { token }   = useAuth();
  const isAdmin     = useSelector((state: RootState) => state.auth.isAdmin);
  const subscriptionActive = useSelector((state: RootState) => state.colors.subscription.active);
  const navigate    = useNavigate();
  const location    = useLocation();
  const dispatch    = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [points,       setPoints]       = useState<Point[]>([]);
  const [zones,        setZones]        = useState<Zone[]>([]);
  const locationState  = location.state as { zoom?: number; x?: number; y?: number } | null;
  const searchX = Number(searchParams.get('x'));
  const searchY = Number(searchParams.get('y'));
  const searchZoom = Number(searchParams.get('zoom'));
  const initZoom = locationState?.zoom ?? (!Number.isNaN(searchZoom) && searchZoom > 0 ? searchZoom : (parseFloat(localStorage.getItem('gameZoom') ?? '10') || 10));
  useEffect(() => {
    if (pointId) localStorage.setItem('lastGamePointId', pointId);
  }, [pointId]);
  const [viewport,     setViewport]     = useState<Viewport>({
    x: locationState?.x ?? (!Number.isNaN(searchX) ? searchX : 0),
    y: locationState?.y ?? (!Number.isNaN(searchY) ? searchY : 0),
    zoom: initZoom,
  });
  const [myPoint,      setMyPoint]      = useState<Point | null>(null);
  const [showGrid,     setShowGrid]     = useState(() => localStorage.getItem('showGrid') === 'true');
  const [showZones,    setShowZones]    = useState(() => localStorage.getItem('showZones') === 'true');
  const [controlLayout, setControlLayout] = useState<ControlLayout>(readControlLayout);
  const [controlHand,   setControlHand]   = useState<ControlHand>(readControlHand);
  const [dpadSize,      setDpadSize]      = useState<DpadSize>(readDpadSize);
  const [gridBrightness, setGridBrightness] = useState<number>(() => {
    const n = parseInt(localStorage.getItem('gridBrightness') || '', 10);
    return GRID_BRIGHTNESS_LEVELS.includes(n) ? n : 1;
  });
  const [shareCopied,  setShareCopied]  = useState(false);
  const [creatingPoint,    setCreatingPoint]    = useState(false);
  const [deletingPoint,    setDeletingPoint]    = useState(false);
  const [showHelp,         setShowHelp]         = useState(false);
  const [menuOpen,         setMenuOpen]         = useState(false);
  const [colorOpen,        setColorOpen]        = useState(false);
  const [maxPoints,    setMaxPoints]    = useState<number | null>(null);
  const [totalPoints,  setTotalPoints]  = useState<number | null>(null);
  const [windowSize, setWindowSize] = useState({
    width : window.innerWidth,
    height: window.innerHeight,
  });

  // Zone creation modal
  const [pixelSelectMode, setPixelSelectMode] = useState(false);

  // ── "Go to" mode ───────────────────────────────────────────────
  // Tap a cell to drop a destination marker, then confirm to auto-walk there one
  // cell at a time. A tap only places a harmless marker — the actual move is gated
  // behind an explicit GO, so it never fights camera pan/pinch. Any manual move cancels it.
  const [gotoMode,    setGotoMode]    = useState(false);
  const [gotoTarget,  setGotoTarget]  = useState<{ x: number; y: number } | null>(null);
  const [gotoWalking, setGotoWalking] = useState(false);
  const gotoModeRef   = useRef(false);
  const gotoTargetRef = useRef<{ x: number; y: number } | null>(null);
  const gotoTimerRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const gotoStallRef  = useRef(0);
  const gotoFlipRef   = useRef(false);   // flip preferred axis when blocked by a wall
  const gotoStepRef   = useRef<() => void>(() => {});
  const cancelGotoRef = useRef<() => void>(() => {});

  // ── Long-press handler: switch to own pixel at that world coord ──
  const handleLongPress = useCallback((worldX: number, worldY: number) => {
    const wx = Math.round(worldX);
    const wy = Math.round(worldY);
    const hit = points.find(p =>
      p.user_id === myPoint?.user_id &&
      p.id !== Number(pointId) &&
      p.x === wx && p.y === wy,
    );
    if (hit) {
      navigate(`/game/${hit.id}`, { state: { zoom: vpRef.current.zoom } });
    } else {
      setPixelSelectMode(true);
    }
  }, [points, myPoint, pointId, navigate]);

  const [showZoneModal,     setShowZoneModal]     = useState(false);
  const [zoneDeleteTarget,  setZoneDeleteTarget]  = useState<Zone | null>(null);
  const [zoneDeletingId,    setZoneDeletingId]    = useState<number | null>(null);
  const [zoneName,      setZoneName]      = useState('');
  const [zoneX1,        setZoneX1]        = useState('');
  const [zoneY1,        setZoneY1]        = useState('');
  const [zoneX2,        setZoneX2]        = useState('');
  const [zoneY2,        setZoneY2]        = useState('');
  const [zoneTimeout,   setZoneTimeout]   = useState('30');
  const [creatingZone,  setCreatingZone]  = useState(false);
  const [zoneDrawMode,  setZoneDrawMode]  = useState(false);
  const [zonePrice,     setZonePrice]     = useState<ZonePriceResult | null>(null);
  const [zonePriceLoading, setZonePriceLoading] = useState(false);
  const [zoneFreeUsed,  setZoneFreeUsed]  = useState(0);
  const zonePriceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCreatePointRef = useRef<() => void>(() => {});
  const handleDeletePointRef = useRef<() => void>(() => {});

  // ── Say feature ────────────────────────────────────────────────
  const { speeches, handleSaidEvent, clearSpeeches } = useSpeechBubbles();
  const [showSayInput, setShowSayInput] = useState(false);
  const [sayText,      setSayText]      = useState('');
  const [jumpOnSay,    setJumpOnSay]    = useState(false);
  const [sayStyle,     setSayStyle]     = useState<SpeechBubbleStyle>(DEFAULT_SPEECH_STYLE);
  const worldCanvasRef = useRef<WorldCanvasHandle>(null);

  const [wsStatus, setWsStatus] = useState<WsStatusInfo | null>(null);
  const wsRef           = useRef<WebSocketClient | null>(null);
  const vpRef           = useRef(viewport);
  vpRef.current         = viewport;
  const myPointRef      = useRef(myPoint);
  myPointRef.current    = myPoint;
  const pointsRef       = useRef(points);
  pointsRef.current     = points;
  // Outstanding client-predicted moves — server ack decrements.
  // While >0 we trust the client's optimistic position over server echoes.
  const pendingMovesRef = useRef<number>(0);
  const windowSizeRef   = useRef(windowSize);
  windowSizeRef.current = windowSize;
  const resubscribeTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zoneRefreshTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMoveSubscribeAt = useRef<number>(0);
  const hasInitRef          = useRef(false);
  const subscribedAreaRef   = useRef<{ x_min: number; y_min: number; x_max: number; y_max: number } | null>(null);
  // Accumulators for incoming point updates — both flushed together once per
  // animation frame so many events in the same tick collapse into one render.
  const pendingMergeRef = useRef<Map<number, Point>>(new Map());
  // Position-only updates from individual 'moved' events for foreign points.
  const pendingMovedRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const mergeRafRef     = useRef<number | null>(null);

  // Perf metrics — updated via direct DOM writes to avoid React re-renders
  const fpsDivRef      = useRef<HTMLDivElement>(null);
  const rttDivRef      = useRef<HTMLDivElement>(null);
  const snapDivRef     = useRef<HTMLDivElement>(null);
  const moveStampRef   = useRef<number>(0);
  const rttSamplesRef  = useRef<number[]>([]);
  // Client-side throttle: 50ms gives 20ms margin over server's 30ms cooldown,
  // absorbing typical network jitter and preventing "too fast" rejections.
  const lastMoveSentRef = useRef<number>(0);
  // Last position confirmed by a server 'moved' ack for the own point.
  // Used to roll back optimistic predictions when the server rejects a move.
  const serverPosRef = useRef<{ x: number; y: number } | null>(null);

  // ── Snap diagnostics ────────────────────────────────────────────
  // Tracks reasons the client's predicted position diverges from the server's
  // authoritative position, forcing a visible "throwback" on reconcile.
  const snapStatsRef = useRef({
    snaps        : 0,   // total reconcile snaps
    totalCells   : 0,   // cumulative |Δx|+|Δy| across snaps (manhattan)
    maxCells     : 0,   // largest single snap
    tooFast      : 0,   // server "too fast" rejections
    throttled    : 0,   // client-side throttle drops
    predictOnly  : 0,   // blocked by client-predicted wall (never sent)
    sent         : 0,   // moves actually sent
    ackedOk      : 0,   // final acks that matched prediction (no snap)
    lastSnap     : '',  // textual last-snap detail
  });
  const renderSnapStats = useCallback(() => {
    const s = snapStatsRef.current;
    if (!snapDivRef.current) return;
    const avg = s.snaps > 0 ? (s.totalCells / s.snaps).toFixed(1) : '0';
    snapDivRef.current.textContent =
      `Snap: ${s.snaps}×  avgΔ=${avg}  maxΔ=${s.maxCells}  ` +
      `tooFast=${s.tooFast}  thrott=${s.throttled}  ` +
      `sent=${s.sent}  okAck=${s.ackedOk}` +
      (s.lastSnap ? `  last: ${s.lastSnap}` : '');
  }, []);
  // Points updated by 'moved' events since the last subscribe — their positions
  // are more current than any in-flight init response; we skip position overwrite for them.
  const movedSinceSubscribeRef = useRef<Set<number>>(new Set());

  // ── Fetch user limits + color catalog ─────────────────────────
  useEffect(() => {
    getMe().then(res => setMaxPoints(res.data.max_points)).catch(() => {});
    getUserPoints().then(res => setTotalPoints(res.data.length)).catch(() => {});
    dispatch(fetchColorCatalog());
  }, [dispatch]);

  // ── Handle return from payments ───────────────────────────────
  useEffect(() => {
    const result = searchParams.get('payment');
    if (result === 'color_success') {
      dispatch(fetchColorCatalog());
      trackEvent('payment_success', { kind: 'color', from: 'game' });
      toastSuccess(t('shop.paymentSuccess'));
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      setSearchParams(next, { replace: true });
    } else if (result === 'zone_success') {
      trackEvent('payment_success', { kind: 'zone', from: 'game' });
      toastSuccess(t('zone.paymentSuccess'));
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      setSearchParams(next, { replace: true });
    } else if (result === 'fail') {
      trackEvent('payment_fail', { from: 'game' });
      toastError(t('shop.paymentFail'));
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, dispatch, t]);

  // ── Show help on first visit ───────────────────────────────────
  useEffect(() => {
    if (!localStorage.getItem('gameHelpSeen')) {
      setShowHelp(true);
      localStorage.setItem('gameHelpSeen', '1');
    }
  }, []);

  // ── Window resize ──────────────────────────────────────────────
  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── FPS counter — direct DOM, no React state ──────────────────
  useEffect(() => {
    let frames = 0;
    let maxFrameMs = 0;
    let lastTickTime = performance.now();
    let lastTime = performance.now();
    let rafId: number;
    const tick = () => {
      const now = performance.now();
      const frameMs = now - lastTickTime;
      lastTickTime = now;
      if (frameMs > maxFrameMs) maxFrameMs = frameMs;
      frames++;
      if (now - lastTime >= 1000) {
        const elapsed = now - lastTime;
        const fps = Math.round(frames * 1000 / elapsed);
        const avgMs = Math.round(elapsed / frames);
        const pts = pointsRef.current.length;
        if (fpsDivRef.current) {
          fpsDivRef.current.textContent =
            `FPS: ${fps}  avg: ${avgMs}ms  worst: ${Math.round(maxFrameMs)}ms  pts: ${pts}`;
        }
        frames = 0;
        lastTime = now;
        maxFrameMs = 0;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // ── Fetch zones for the current viewport area ──────────────────
  const fetchZones = useCallback((vp: Viewport) => {
    const { width: w, height: h } = windowSizeRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 20;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 20;
    getZonesInArea(
      Math.round(vp.x) - halfW,
      Math.round(vp.y) - halfH,
      Math.round(vp.x) + halfW,
      Math.round(vp.y) + halfH,
    ).then(res => setZones(res.data)).catch(() => {});
  }, []);

  // ── WS subscribe helper ────────────────────────────────────────
  const sendSubscribe = useCallback((vp: Viewport) => {
    const { width: w, height: h } = windowSizeRef.current;
    const halfW = Math.ceil(w / 2 / vp.zoom) + 10;
    const halfH = Math.ceil(h / 2 / vp.zoom) + 10;
    const area = {
      x_min: Math.round(vp.x) - halfW,
      y_min: Math.round(vp.y) - halfH,
      x_max: Math.round(vp.x) + halfW,
      y_max: Math.round(vp.y) + halfH,
    };
    subscribedAreaRef.current = area;
    movedSinceSubscribeRef.current.clear();
    wsRef.current?.send({ type: 'subscribe', area });
  }, []);

  // ── Say command ────────────────────────────────────────────────
  const handleSayOpen = useCallback(() => {
    setShowSayInput(v => {
      if (!v) {
        setSayText('');
        setJumpOnSay(false);
        setSayStyle(DEFAULT_SPEECH_STYLE);
      }
      return !v;
    });
  }, []);

  const handleJump = useCallback(() => {
    trackEvent('game_jump', { point_id: pointId });
    wsRef.current?.send({ type: 'jump' });
  }, [pointId]);

  useEffect(() => {
    if (!subscriptionActive && isPremiumSpeechStyle(sayStyle)) {
      setSayStyle(DEFAULT_SPEECH_STYLE);
    }
  }, [subscriptionActive, sayStyle]);

  const handleSaySubmit = useCallback((text: string, withJump: boolean, style: SpeechBubbleStyle) => {
    const trimmed = text.trim();
    if (trimmed) {
      const resolvedStyle = !subscriptionActive && isPremiumSpeechStyle(style)
        ? DEFAULT_SPEECH_STYLE
        : style;
      trackEvent('game_say', {
        point_id    : pointId,
        length      : trimmed.length,
        style       : resolvedStyle,
        with_jump   : withJump,
        is_premium  : isPremiumSpeechStyle(resolvedStyle),
      });
      wsRef.current?.send({ type: 'say', text: trimmed, style: resolvedStyle });
      if (withJump) wsRef.current?.send({ type: 'jump' });
    }
    setSayText('');
    setSayStyle(DEFAULT_SPEECH_STYLE);
    setShowSayInput(false);
  }, [subscriptionActive, pointId]);

  // ── Keyboard controls ──────────────────────────────────────────
  const handleMove = useCallback((direction: string) => {
    const cur = myPointRef.current;
    const [dx, dy] = MOVE_DIRS[direction] ?? [0, 0];
    if (dx === 0 && dy === 0) return;

    // Client-side throttle matches server cooldown (30ms) with small margin.
    // Dropping here keeps prediction and server state in sync — a rejected
    // move would leave the client one cell ahead, forcing a snap-back on reconcile.
    const now = performance.now();
    if (now - lastMoveSentRef.current < 50) {
      snapStatsRef.current.throttled++;
      renderSnapStats();
      return;
    }
    lastMoveSentRef.current = now;

    // Optimistic prediction: mirror server's computeNewPosition using the
    // points we currently know about. Jump over occupied cells, stop at impassable.
    if (cur) {
      const occupied = new Map<string, Point>();
      for (const p of pointsRef.current) {
        if (p.id !== cur.id) occupied.set(`${p.x},${p.y}`, p);
      }

      let px = cur.x + dx;
      let py = cur.y + dy;
      let blocked = false;
      for (let step = 0; step < 128; step++) {
        const hit = occupied.get(`${px},${py}`);
        if (!hit) break;
        if (hit.is_impassable) { blocked = true; break; }
        px += dx;
        py += dy;
      }

      // Blocked by wall — don't spend a network round-trip; local state is
      // already consistent with the server (which would refuse this move too).
      if (blocked) {
        snapStatsRef.current.predictOnly++;
        renderSnapStats();
        return;
      }

      if (px !== cur.x || py !== cur.y) {
        myPointRef.current = { ...cur, x: px, y: py };
        worldCanvasRef.current?.predictMove(cur.id, px, py);
        setMyPoint(p => p ? { ...p, x: px, y: py } : p);
        setViewport(vp => ({ ...vp, x: px, y: py }));
        setPoints(prev => {
          const idx = prev.findIndex(p => p.id === cur.id);
          if (idx < 0) return prev;
          const next = prev.slice();
          next[idx] = { ...prev[idx], x: px, y: py };
          return next;
        });
      }
    }

    moveStampRef.current = performance.now();
    pendingMovesRef.current++;
    snapStatsRef.current.sent++;
    renderSnapStats();
    wsRef.current?.send({ type: 'move', direction });
    moveTracker.recordMove();
  }, [renderSnapStats, moveTracker]);

  useEffect(() => {
    const map: Record<string, string> = {
      ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
      w: 'up',       s: 'down',         a: 'left',          d: 'right',
      // Russian keyboard layout equivalents
      'ц': 'up',     'ы': 'down',       'ф': 'left',        'в': 'right',
      'Ц': 'up',     'Ы': 'down',       'Ф': 'left',        'В': 'right',
    };
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
      const inputFocused = tag === 'INPUT' || tag === 'TEXTAREA';
      if (inputFocused) return;
      if (map[e.key]) { e.preventDefault(); cancelGotoRef.current(); handleMove(map[e.key]); }
      if (e.key === 'Escape') cancelGotoRef.current();
      if (e.key === 'g' || e.key === 'G' || e.key === 'п' || e.key === 'П') setShowGrid(v => { const next = !v; localStorage.setItem('showGrid', String(next)); return next; });
      if (e.key === 'c' || e.key === 'C' || e.key === 'с' || e.key === 'С') setColorOpen(true);
      if (e.key === 'j' || e.key === 'J' || e.key === 'о' || e.key === 'О') { e.preventDefault(); handleJump(); }
      if (e.key === 't' || e.key === 'T' || e.key === 'е' || e.key === 'Е') {
        e.preventDefault();
        if (e.shiftKey) {
          // Shift+T: open say with jump pre-enabled
          setSayText('');
          setJumpOnSay(true);
          setSayStyle(DEFAULT_SPEECH_STYLE);
          setShowSayInput(true);
        } else {
          setShowSayInput(v => {
            if (!v) {
              setSayText('');
              setJumpOnSay(false);
              setSayStyle(DEFAULT_SPEECH_STYLE);
            }
            return !v;
          });
        }
      }
      if (e.key === ' ') { e.preventDefault(); handleCreatePointRef.current(); }
      if (e.key === 'Delete') { e.preventDefault(); handleDeletePointRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleMove, handleJump]);

  // ── Viewport changes from drag / wheel ─────────────────────────
  const handleViewportChange = useCallback((newVp: Viewport) => {
    setViewport(newVp);
    localStorage.setItem('gameZoom', String(newVp.zoom));
    if (resubscribeTimer.current) clearTimeout(resubscribeTimer.current);
    resubscribeTimer.current = setTimeout(() => sendSubscribe(newVp), 250);
    if (zoneRefreshTimer.current) clearTimeout(zoneRefreshTimer.current);
    zoneRefreshTimer.current = setTimeout(() => fetchZones(newVp), 500);
  }, [sendSubscribe, fetchZones]);

  // ── WebSocket ──────────────────────────────────────────────────
  useEffect(() => {
    if (!token || !pointId) return;
    hasInitRef.current = false;

    // Flush both pending accumulators in one setPoints call — called from rAF.
    const flushPendingUpdates = () => {
      mergeRafRef.current = null;
      const updates = Array.from(pendingMergeRef.current.values());
      pendingMergeRef.current.clear();
      const movedPositions = new Map(pendingMovedRef.current);
      pendingMovedRef.current.clear();
      if (updates.length === 0 && movedPositions.size === 0) return;
      const ownId = Number(pointId);
      setPoints(prev => {
        const map = new Map(prev.map((p: Point) => [p.id, p]));
        let changed = false;
        updates.forEach(p => {
          const existing = map.get(p.id);
          if (p.id === ownId) {
            // Own position is client-authoritative — only sync color/flags.
            if (!existing) {
              map.set(p.id, p);
              changed = true;
            } else if (existing.color !== p.color || existing.is_impassable !== p.is_impassable) {
              map.set(p.id, { ...existing, color: p.color, is_impassable: p.is_impassable });
              changed = true;
            }
          } else if (!existing || !samePoint(existing, p)) {
            map.set(p.id, p);
            changed = true;
          }
        });
        // Apply batched position-only moves from individual 'moved' events.
        movedPositions.forEach(({ x, y }, id) => {
          if (id === ownId) return;
          const existing = map.get(id);
          if (!existing || (existing.x === x && existing.y === y)) return;
          map.set(id, { ...existing, x, y });
          changed = true;
        });
        return changed ? Array.from(map.values()) : prev;
      });
    };

    const { width: w, height: h } = windowSizeRef.current;
    const halfW = Math.ceil(w / 2 / initZoom) + 10;
    const halfH = Math.ceil(h / 2 / initZoom) + 10;
    // Token is delivered via Sec-WebSocket-Protocol (see WebSocketClient).
    // Keeping it out of the URL avoids leaking the JWT into proxy/access logs.
    const url    = `${getWsBase()}?pointId=${pointId}&halfW=${halfW}&halfH=${halfH}`;
    const client = new WebSocketClient(url, (event: WsEvent) => {
      switch (event.type) {
        case 'init': {
          const all = Array.isArray(event.points) ? event.points : [];
          const moved = movedSinceSubscribeRef.current;
          const ownId = Number(pointId);
          setPoints(prev => {
            const map = new Map(prev.map((p: Point) => [p.id, p]));
            // Walking re-subscribes about once a second, and the snapshot that
            // comes back is nearly always identical to what we hold. Returning
            // the same array then skips a re-render and a full renderer rescan
            // of a viewport that can hold tens of thousands of points.
            let changed = false;
            all.forEach((p: Point) => {
              const existing = map.get(p.id);
              if (!existing) {
                map.set(p.id, p);
                changed = true;
                return;
              }
              // Own pixel position is client-authoritative (predicted + reconciled
              // via 'moved' acks). Never let a late init snapshot snap it back —
              // only sync the color.
              if (p.id === ownId) {
                if (existing.color !== p.color || existing.is_impassable !== p.is_impassable) {
                  map.set(p.id, { ...existing, color: p.color, is_impassable: p.is_impassable });
                  changed = true;
                }
                return;
              }
              if (moved.has(p.id)) {
                // This foreign point was updated by a 'moved' event after the
                // subscribe was sent; keep the newer position, sync color.
                if (existing.color !== p.color) {
                  map.set(p.id, { ...existing, color: p.color });
                  changed = true;
                }
              } else if (!samePoint(existing, p)) {
                map.set(p.id, p);
                changed = true;
              }
            });
            return changed ? Array.from(map.values()) : prev;
          });
          if (!hasInitRef.current) {
            hasInitRef.current = true;
            const mine = all.find((p: Point) => p.id === Number(pointId));
            if (mine) {
              serverPosRef.current = { x: mine.x, y: mine.y };
              setMyPoint(mine);
              const initVp: Viewport = { x: mine.x, y: mine.y, zoom: initZoom };
              setViewport(initVp);
              // No sendSubscribe here — the WS connection was already opened with the
              // correct area; a redundant subscribe would cause a stale init to arrive
              // after the player has moved, regressing their position.
              fetchZones(initVp);
            }
          }
          break;
        }
        case 'points_update': {
          const incoming: Point[] = Array.isArray(event.points) ? event.points : [];
          incoming.forEach(p => pendingMergeRef.current.set(p.id, p));
          if (mergeRafRef.current === null) {
            mergeRafRef.current = requestAnimationFrame(flushPendingUpdates);
          }
          break;
        }
        case 'error':
          // An error means the server rejected the most recent in-flight move
          // (usually rate-limited). Decrement so pendingMoves stays accurate.
          if (pendingMovesRef.current > 0) pendingMovesRef.current--;
          if (event.message === 'too fast') {
            snapStatsRef.current.tooFast++;
            renderSnapStats();
            // All in-flight moves resolved — prediction is now stale by 1 cell.
            // Roll back to last server-confirmed position immediately so the
            // player sees an instant correction instead of a snap on the next ack.
            if (pendingMovesRef.current === 0 && serverPosRef.current) {
              const srv = serverPosRef.current;
              const cur = myPointRef.current;
              if (cur && (cur.x !== srv.x || cur.y !== srv.y)) {
                myPointRef.current = { ...cur, x: srv.x, y: srv.y };
                worldCanvasRef.current?.predictMove(cur.id, srv.x, srv.y);
                setPoints(prev => {
                  const idx = prev.findIndex(p => p.id === cur.id);
                  if (idx < 0) return prev;
                  const next = prev.slice();
                  next[idx] = { ...next[idx], x: srv.x, y: srv.y };
                  return next;
                });
                setMyPoint(p => p ? { ...p, x: srv.x, y: srv.y } : p);
                setViewport(vp => ({ ...vp, x: srv.x, y: srv.y }));
              }
            }
          } else if (event.message === 'speech_style_locked') {
            toastError(t('game.speechStyleLocked'));
          } else if (event.message) {
            toastError(event.message);
          }
          break;
        case 'moved': {
          movedSinceSubscribeRef.current.add(event.pointId);
          const isOwnPoint = event.pointId === Number(pointId);

          if (isOwnPoint) {
            pendingMovesRef.current = Math.max(0, pendingMovesRef.current - 1);
            if (moveStampRef.current > 0) {
              const rtt = Math.round(performance.now() - moveStampRef.current);
              const srv = event.serverMs ?? 0;
              const net = Math.max(0, rtt - srv);
              const samples = rttSamplesRef.current;
              samples.push(rtt);
              if (samples.length > 10) samples.shift();
              const avg = Math.round(samples.reduce((a, b) => a + b, 0) / samples.length);
              if (rttDivRef.current) rttDivRef.current.textContent = `RTT: ${rtt}ms ~${avg}ms (srv ${srv}ms net ${net}ms)`;
            }

            // While more predicted moves are in flight, the client is ahead of
            // server — don't snap anything from this intermediate ack.
            if (pendingMovesRef.current > 0) break;

            // Final ack: reconcile only if server disagrees with our optimistic
            // position (rare — happens when client misestimated jump-over).
            const cur = myPointRef.current;
            if (cur && cur.x === event.newX && cur.y === event.newY) {
              // Perfect match — no re-render needed, just schedule resubscribe.
              serverPosRef.current = { x: event.newX, y: event.newY };
              snapStatsRef.current.ackedOk++;
              renderSnapStats();
              const now = Date.now();
              if (now - lastMoveSubscribeAt.current >= 1000) {
                lastMoveSubscribeAt.current = now;
                sendSubscribe(vpRef.current);
              }
              if (resubscribeTimer.current) clearTimeout(resubscribeTimer.current);
              resubscribeTimer.current = setTimeout(() => sendSubscribe(vpRef.current), 250);
              break;
            }
            // Snap to server truth — record diagnostic info.
            if (cur) {
              const dxCells = event.newX - cur.x;
              const dyCells = event.newY - cur.y;
              const dist    = Math.abs(dxCells) + Math.abs(dyCells);
              const s = snapStatsRef.current;
              s.snaps++;
              s.totalCells += dist;
              if (dist > s.maxCells) s.maxCells = dist;
              s.lastSnap =
                `pred(${cur.x},${cur.y})→srv(${event.newX},${event.newY}) ` +
                `Δ=(${dxCells},${dyCells}) dist=${dist} ` +
                `pending=${pendingMovesRef.current} ` +
                `tooFastSince=${s.tooFast}`;
              renderSnapStats();
              // eslint-disable-next-line no-console
              console.warn('[snap]', s.lastSnap);
            }
            serverPosRef.current = { x: event.newX, y: event.newY };
            myPointRef.current = cur ? { ...cur, x: event.newX, y: event.newY } : cur;
            worldCanvasRef.current?.predictMove(event.pointId, event.newX, event.newY);
            setPoints(prev => prev.map(p => p.id === event.pointId ? { ...p, x: event.newX, y: event.newY } : p));
            setMyPoint(p => (p ? { ...p, x: event.newX, y: event.newY } : p));
            const newVp: Viewport = { ...vpRef.current, x: event.newX, y: event.newY };
            setViewport(newVp);
            const now = Date.now();
            if (now - lastMoveSubscribeAt.current >= 1000) {
              lastMoveSubscribeAt.current = now;
              sendSubscribe(newVp);
            }
            if (resubscribeTimer.current) clearTimeout(resubscribeTimer.current);
            resubscribeTimer.current = setTimeout(() => sendSubscribe(newVp), 250);
            break;
          }

          // Foreign point moved — batch into the same rAF flush as points_update
          // to avoid O(N) React renders for every individual move event.
          pendingMovedRef.current.set(event.pointId, { x: event.newX, y: event.newY });
          if (mergeRafRef.current === null) {
            mergeRafRef.current = requestAnimationFrame(flushPendingUpdates);
          }
          break;
        }
        case 'said':
          handleSaidEvent(event.point_id, event.text, event.style);
          break;
        case 'jumped':
          worldCanvasRef.current?.triggerJump(event.point_id);
          break;
        case 'point_removed': {
          const removedId = event.point_id;
          setPoints(prev => prev.filter(p => p.id !== removedId));
          break;
        }
        case 'eviction_warning':
          toastInfo(t('zone.evictionWarning', { name: event.zone_name, seconds: event.seconds_left }));
          break;
        case 'evicted': {
          toastError(t('zone.evicted', { name: event.zone_name }));
          setPoints(prev =>
            prev.map(p => p.id === Number(pointId) ? { ...p, x: event.new_x, y: event.new_y } : p),
          );
          setMyPoint(p => (p ? { ...p, x: event.new_x, y: event.new_y } : p));
          const evictedVp: Viewport = { ...vpRef.current, x: event.new_x, y: event.new_y };
          setViewport(evictedVp);
          sendSubscribe(evictedVp);
          fetchZones(evictedVp);
          break;
        }
      }
    }, setWsStatus, token);

    wsRef.current = client;
    return () => {
      client.close();
      wsRef.current = null;
      if (resubscribeTimer.current) clearTimeout(resubscribeTimer.current);
      if (zoneRefreshTimer.current) clearTimeout(zoneRefreshTimer.current);
      if (mergeRafRef.current !== null) {
        cancelAnimationFrame(mergeRafRef.current);
        mergeRafRef.current = null;
      }
      pendingMergeRef.current.clear();
      movedSinceSubscribeRef.current.clear();
      clearSpeeches();
    };
  }, [pointId, token, sendSubscribe, fetchZones, t]);

  // ── Share location ─────────────────────────────────────────────
  const handleShare = useCallback(() => {
    const vp = vpRef.current;
    const url = `${window.location.origin}/?x=${Math.round(vp.x)}&y=${Math.round(vp.y)}&zoom=${vp.zoom.toFixed(1)}`;
    navigator.clipboard.writeText(url).then(() => {
      trackEvent('game_share', { point_id: pointId, zoom: Number(vp.zoom.toFixed(1)) });
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  }, [pointId]);

  // ── Create new point ───────────────────────────────────────────
  const handleCreatePoint = useCallback(async () => {
    if (creatingPoint) return;
    setCreatingPoint(true);
    try {
      const res = await createPointApi('My Pixel', myPoint?.x, myPoint?.y);
      const newPoint = res.data;
      trackEvent('point_create', { point_id: newPoint.id, from: 'game' });
      setTotalPoints(prev => (prev !== null ? prev + 1 : prev));
      if (myPoint?.color && myPoint.color !== newPoint.color) {
        await updatePointApi(newPoint.id, newPoint.name, myPoint.color).catch(() => {});
      }
      navigate(`/game/${newPoint.id}`, { state: { zoom: vpRef.current.zoom, x: newPoint.x, y: newPoint.y } });
    } catch (e: any) {
      toastError(e?.response?.data?.error || e?.message || t('game.errCreate'));
    } finally {
      setCreatingPoint(false);
    }
  }, [navigate, myPoint, t, creatingPoint]);
  handleCreatePointRef.current = handleCreatePoint;

  // ── Delete current point ───────────────────────────────────────
  const handleDeletePoint = useCallback(async () => {
    if (deletingPoint) return;
    setDeletingPoint(true);

    try {
      await deletePointApi(Number(pointId));
      trackEvent('point_delete', { point_id: pointId, from: 'game' });
      setPoints(prev => prev.filter(p => p.id !== Number(pointId)));
      setTotalPoints(prev => (prev !== null ? Math.max(0, prev - 1) : prev));
      const res = await getUserPoints();
      const remaining = res.data;
      if (remaining.length > 0) {
        const last = remaining.reduce((a, b) =>
          new Date(a.created_at) > new Date(b.created_at) ? a : b,
        );
        navigate(`/game/${last.id}`, { state: { zoom: vpRef.current.zoom, x: last.x, y: last.y } });
      } else {
        navigate('/dashboard');
      }
    } catch (e: any) {
      toastError(e?.response?.data?.error || e?.message || t('game.errDelete'));
    } finally {
      setDeletingPoint(false);
    }
  }, [deletingPoint, pointId, navigate, t]);
  handleDeletePointRef.current = handleDeletePoint;


  // ── Color apply ────────────────────────────────────────────────
  const submitColor = useCallback(async (color: string) => {
    if (!myPoint) return;
    try {
      const res = await updatePointApi(myPoint.id, myPoint.name, color);
      const updated = res.data;
      trackEvent('point_color_change', { point_id: updated.id, color: updated.color, from: 'game' });
      setMyPoint(prev => (prev ? { ...prev, color: updated.color } : prev));
      setPoints(prev => prev.map(p => p.id === updated.id ? { ...p, color: updated.color } : p));
    } catch (e: any) {
      toastError(e?.response?.data?.error || e?.message || t('game.errColor'));
    }
  }, [myPoint, t]);

  // ── Impassable toggle ──────────────────────────────────────────
  const [togglingImpassable, setTogglingImpassable] = useState(false);
  const toggleImpassable = useCallback(async () => {
    if (!myPoint || togglingImpassable) return;
    setTogglingImpassable(true);
    try {
      const res = await updatePointApi(myPoint.id, myPoint.name, undefined, !myPoint.is_impassable);
      const updated = res.data;
      trackEvent('point_impassable_toggle', { point_id: updated.id, is_impassable: updated.is_impassable });
      setMyPoint(prev => (prev ? { ...prev, is_impassable: updated.is_impassable } : prev));
      setPoints(prev => prev.map(p => p.id === updated.id ? { ...p, is_impassable: updated.is_impassable } : p));
    } catch (e: any) {
      toastError(e?.response?.data?.error || e?.message || t('game.errImpassable'));
    } finally {
      setTogglingImpassable(false);
    }
  }, [myPoint, togglingImpassable, t]);

  // ── Tap-zones movement (the `taps` preset) ─────────────────────
  // Fires on a field TAP only (WorldCanvas calls onWorldClick when the finger
  // didn't drag — so camera panning is untouched). The screen region of the tap
  // relative to centre picks the direction; a small central dead-zone is a no-op.
  const handleFieldTap = useCallback((worldX: number, worldY: number) => {
    if (controlLayout !== 'taps') return;
    const vp = vpRef.current;
    const { width: w, height: h } = windowSizeRef.current;
    // Exact inverse of the canvas transform → tap offset from screen centre.
    const dxs = (worldX - vp.x) * vp.zoom;       // +right
    const dys = (vp.y - worldY) * vp.zoom;       // +down (screen Y grows down)
    if (Math.hypot(dxs, dys) < Math.min(w, h) * 0.1) return; // dead-zone
    const dir = Math.abs(dxs) > Math.abs(dys)
      ? (dxs > 0 ? 'right' : 'left')
      : (dys > 0 ? 'down'  : 'up');
    handleMove(dir);
  }, [controlLayout, handleMove]);

  // ── Control settings: layout / hand / d-pad size ───────────────
  const selectControlLayout = useCallback((m: ControlLayout) => {
    setControlLayout(m);
    localStorage.setItem('gameControls', m);
    trackEvent('control_layout_change', { layout: m });
  }, []);
  const selectControlHand = useCallback((h: ControlHand) => {
    setControlHand(h);
    localStorage.setItem('gameControlsHand', h);
    trackEvent('control_hand_change', { hand: h });
  }, []);
  const selectDpadSize = useCallback((s: DpadSize) => {
    setDpadSize(s);
    localStorage.setItem('gameControlsSize', s);
    trackEvent('control_dpad_size_change', { size: s });
  }, []);
  const selectGridBrightness = useCallback((n: number) => {
    setGridBrightness(n);
    localStorage.setItem('gridBrightness', String(n));
  }, []);

  // ── "Go to" controls ───────────────────────────────────────────
  const cancelGoto = useCallback(() => {
    if (gotoTimerRef.current) { clearInterval(gotoTimerRef.current); gotoTimerRef.current = null; }
    gotoModeRef.current   = false;
    gotoTargetRef.current = null;
    gotoStallRef.current  = 0;
    gotoFlipRef.current   = false;
    setGotoWalking(false);
    setGotoTarget(null);
    setGotoMode(false);
  }, []);
  cancelGotoRef.current = cancelGoto;

  const toggleGotoMode = useCallback(() => {
    if (gotoModeRef.current) { cancelGoto(); return; }
    gotoModeRef.current   = true;
    gotoTargetRef.current = null;
    setGotoTarget(null);
    setGotoWalking(false);
    setGotoMode(true);
    toastInfo(t('game.gotoHint'));
  }, [cancelGoto, t]);

  // One greedy step toward the destination. The server auto-jumps over occupied
  // cells, so only impassable walls actually block. On a blocked step we flip the
  // preferred axis to route around it, and abort after a streak of no-progress ticks.
  const gotoStep = useCallback(() => {
    const target = gotoTargetRef.current;
    const cur    = myPointRef.current;
    if (!target || !cur) { cancelGoto(); return; }
    const dx = target.x - cur.x;
    const dy = target.y - cur.y;
    if (dx === 0 && dy === 0) { cancelGoto(); return; }   // arrived

    const wantX = gotoFlipRef.current ? Math.abs(dx) < Math.abs(dy) : Math.abs(dx) >= Math.abs(dy);
    let dir: string;
    if (wantX && dx !== 0)   dir = dx > 0 ? 'right' : 'left';
    else if (dy !== 0)       dir = dy > 0 ? 'up' : 'down';
    else                     dir = dx > 0 ? 'right' : 'left';

    const bx = cur.x, by = cur.y;
    handleMove(dir);
    const after = myPointRef.current;
    if (after && after.x === bx && after.y === by) {
      // No progress → wall in the way. Try the other axis next tick; give up if stuck.
      gotoFlipRef.current  = !gotoFlipRef.current;
      gotoStallRef.current += 1;
      if (gotoStallRef.current > 12) { toastInfo(t('game.gotoUnreachable')); cancelGoto(); }
    } else {
      gotoFlipRef.current  = false;
      gotoStallRef.current = 0;
    }
  }, [handleMove, cancelGoto, t]);
  gotoStepRef.current = gotoStep;

  const startGotoWalk = useCallback(() => {
    if (!gotoTargetRef.current) return;
    gotoStallRef.current = 0;
    gotoFlipRef.current  = false;
    setGotoWalking(true);
    trackEvent('game_goto_walk', { point_id: pointId });
    if (gotoTimerRef.current) clearInterval(gotoTimerRef.current);
    // 80ms pace clears the 50ms client move throttle with margin.
    gotoTimerRef.current = setInterval(() => gotoStepRef.current(), 80);
  }, [pointId]);

  // Field tap/click: in go-to mode it (re)places the destination marker — never moves
  // the pixel. Otherwise falls through to taps-preset stepping.
  const handleWorldClick = useCallback((worldX: number, worldY: number) => {
    if (gotoModeRef.current) {
      const cell = { x: Math.round(worldX), y: Math.round(worldY) };
      gotoTargetRef.current = cell;
      setGotoTarget(cell);
      return;
    }
    handleFieldTap(worldX, worldY);
  }, [handleFieldTap]);

  // Stop the walker on unmount.
  useEffect(() => () => { if (gotoTimerRef.current) clearInterval(gotoTimerRef.current); }, []);

  // ── D-pad hold-repeat (manual move cancels any active go-to walk) ──
  const holdUp    = useHoldRepeat(useCallback(() => { cancelGotoRef.current(); handleMove('up');    }, [handleMove]));
  const holdDown  = useHoldRepeat(useCallback(() => { cancelGotoRef.current(); handleMove('down');  }, [handleMove]));
  const holdLeft  = useHoldRepeat(useCallback(() => { cancelGotoRef.current(); handleMove('left');  }, [handleMove]));
  const holdRight = useHoldRepeat(useCallback(() => { cancelGotoRef.current(); handleMove('right'); }, [handleMove]));

  // ── Zoom buttons ───────────────────────────────────────────────
  const zoomBy = useCallback((factor: number) => {
    const newVp: Viewport = {
      ...vpRef.current,
      zoom: Math.max(0.5, Math.min(1000, vpRef.current.zoom * factor)),
    };
    handleViewportChange(newVp);
  }, [handleViewportChange]);

  // ── Zone price: debounced fetch when coords change ─────────────
  useEffect(() => {
    if (!showZoneModal) return;
    const x1 = parseInt(zoneX1, 10);
    const y1 = parseInt(zoneY1, 10);
    const x2 = parseInt(zoneX2, 10);
    const y2 = parseInt(zoneY2, 10);
    if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) { setZonePrice(null); return; }
    if (zonePriceTimer.current) clearTimeout(zonePriceTimer.current);
    setZonePriceLoading(true);
    zonePriceTimer.current = setTimeout(() => {
      getZonePrice(x1, y1, x2, y2)
        .then(res => setZonePrice(res.data))
        .catch(() => setZonePrice(null))
        .finally(() => setZonePriceLoading(false));
    }, 400);
    return () => { if (zonePriceTimer.current) clearTimeout(zonePriceTimer.current); };
  }, [showZoneModal, zoneX1, zoneY1, zoneX2, zoneY2]);

  // ── ESC closes modals ──────────────────────────────────────────
  useEffect(() => {
    if (!showZoneModal && !showHelp && !colorOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowZoneModal(false);
        setShowHelp(false);
        setColorOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showZoneModal, showHelp, colorOpen]);

  // ── Create zone ────────────────────────────────────────────────
  const openZoneModal = useCallback(() => {
    const vp = vpRef.current;
    const cx = Math.round(vp.x);
    const cy = Math.round(vp.y);
    setZoneX1(String(cx - 50));
    setZoneY1(String(cy - 50));
    setZoneX2(String(cx + 50));
    setZoneY2(String(cy + 50));
    setZoneName('');
    setZoneTimeout('30');
    setZonePrice(null);
    getMyZones().then(res => setZoneFreeUsed(res.data.filter(z => z.is_free).length)).catch(() => {});
    setShowZoneModal(true);
  }, []);

  const handleCreateZone = useCallback(async () => {
    const x1 = parseInt(zoneX1, 10);
    const y1 = parseInt(zoneY1, 10);
    const x2 = parseInt(zoneX2, 10);
    const y2 = parseInt(zoneY2, 10);
    const timeout = parseInt(zoneTimeout, 10);
    if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) {
      toastError(t('zone.errInvalidCoords'));
      return;
    }
    setCreatingZone(true);
    const width  = Math.abs(x2 - x1) + 1;
    const height = Math.abs(y2 - y1) + 1;
    trackEvent('zone_create_initiate', {
      area  : width * height,
      width,
      height,
      from  : 'game',
      amount_rub: zonePrice?.amount_rub ?? null,
      is_free: zonePrice?.is_free === true,
    });
    try {
      const zoneCenterX = Math.round((x1 + x2) / 2);
      const zoneCenterY = Math.round((y1 + y2) / 2);
      const returnPath = `/game/${pointId}?x=${zoneCenterX}&y=${zoneCenterY}&zoom=${vpRef.current.zoom}`;
      const res = await createZone(zoneName.trim(), x1, y1, x2, y2, isNaN(timeout) || timeout <= 0 ? 30 : timeout, returnPath);
      const { payment_url } = res.data;
      if (payment_url) {
        window.location.href = payment_url;
      } else {
        trackEvent('zone_create_success', { kind: 'free', from: 'game' });
        setShowZoneModal(false);
        toastSuccess(t('zone.created'));
        fetchZones(vpRef.current);
      }
    } catch (e: any) {
      trackEvent('zone_create_error', { from: 'game', reason: String(e?.response?.data?.error || e?.message || 'unknown').slice(0, 64) });
      toastError(e?.response?.data?.error || e?.message || t('zone.errCreate'));
    } finally {
      setCreatingZone(false);
    }
  }, [zoneName, zoneX1, zoneY1, zoneX2, zoneY2, zoneTimeout, fetchZones, pointId, t, zonePrice]);

  const handleDeleteZone = useCallback(async () => {
    if (!zoneDeleteTarget) return;
    setZoneDeletingId(zoneDeleteTarget.id);
    try {
      await deleteZone(zoneDeleteTarget.id);
      setZones(prev => prev.filter(z => z.id !== zoneDeleteTarget.id));
      toastSuccess(t('zone.deleted'));
      setZoneDeleteTarget(null);
    } catch (e: any) {
      toastError(e?.response?.data?.error || e?.message || t('zone.errDelete'));
    } finally {
      setZoneDeletingId(null);
    }
  }, [zoneDeleteTarget, t]);

  const myUserId = myPoint?.user_id;

  // Single source of truth for the HUD action buttons.
  // Rendered three ways: desktop vertical list, mobile quick-bar (`quick`),
  // and the mobile "⋯" bottom sheet (all of them, labeled).
  type HudAction = {
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    quick?: boolean;       // surfaced in the always-visible mobile quick-bar
    active?: boolean;
    disabled?: boolean;
    danger?: boolean;
    iconColor?: string;
    accent?: string;       // thin colored bar (player color swatch)
  };
  const hudActions: HudAction[] = [
    { key: 'share',  icon: shareCopied ? '✓' : '↗', label: shareCopied ? t('common.copied') : t('common.share'), onClick: handleShare, active: shareCopied },
    { key: 'color',  icon: '■', iconColor: myPoint?.color ?? '#fff', accent: myPoint?.color, label: t('game.colorBtn'), onClick: () => setColorOpen(true), quick: true },
    { key: 'say',    icon: '💬', label: t('game.sayBtn'), onClick: handleSayOpen, active: showSayInput, quick: true },
    { key: 'jump',   icon: '↑', label: t('game.jumpBtn'), onClick: handleJump, quick: true },
    { key: 'goto',   icon: '🎯', label: gotoMode ? t('game.gotoCancel') : t('game.gotoBtn'), onClick: toggleGotoMode, active: gotoMode, quick: true },
    { key: 'add',    icon: creatingPoint ? '…' : '⊕', label: t('game.addPointBtn'), onClick: handleCreatePoint, disabled: creatingPoint, quick: true },
    { key: 'switch', icon: '⇄', label: t('game.switchPixelBtn'), onClick: () => setPixelSelectMode(v => !v), active: pixelSelectMode },
    { key: 'impassable', icon: '⊘', label: myPoint?.is_impassable ? t('game.impassableOn') : t('game.impassableOff'), onClick: toggleImpassable, disabled: togglingImpassable, active: myPoint?.is_impassable },
    { key: 'zone',   icon: '⊞', label: t('zone.reserveBtn'), onClick: openZoneModal },
    { key: 'zonesToggle', icon: showZones ? '◉' : '○', label: showZones ? t('zone.zonesOn') : t('zone.zonesOff'), onClick: () => setShowZones(v => { const next = !v; localStorage.setItem('showZones', String(next)); return next; }), active: showZones },
    { key: 'help',   icon: '?', label: t('game.helpBtn'), onClick: () => setShowHelp(true) },
    { key: 'delete', icon: deletingPoint ? '…' : '✕', label: t('game.deletePointBtn'), onClick: handleDeletePoint, disabled: deletingPoint, danger: true },
  ];

  return (
    <div className={`game-page game-page--controls-${controlLayout} game-page--hand-${controlHand} game-page--dpad-${dpadSize}`}>
      {/* Game canvas */}
      <WorldCanvas
        ref={worldCanvasRef}
        points={points}
        viewport={viewport}
        width={windowSize.width}
        height={windowSize.height}
        onViewportChange={handleViewportChange}
        onLongPress={handleLongPress}
        onWorldClick={handleWorldClick}
      />

      {/* Zone rectangles overlay */}
      {showZones && (
        <ZoneOverlay
          zones={zones}
          viewport={viewport}
          width={windowSize.width}
          height={windowSize.height}
          myUserId={myUserId}
        />
      )}

      {/* Player highlight — pulsing ring over the player pixel */}
      {myPoint && (() => {
        const invertHex = (hex: string) =>
          '#' + (0xFFFFFF ^ parseInt(hex.replace('#', ''), 16)).toString(16).padStart(6, '0');
        // Top-left corner of the 1×1 world-unit pixel rect on screen
        // (matches useWorldRenderer: g.x = point.x, g.y = -point.y, scale = zoom)
        const pxLeft = (myPoint.x - viewport.x) * viewport.zoom + windowSize.width  / 2;
        const pxTop  = (viewport.y - myPoint.y) * viewport.zoom + windowSize.height / 2;
        const pxSize = viewport.zoom;
        // When zoom is tiny, grow the highlight symmetrically around the pixel center
        const minSize = 2;
        const size    = Math.max(pxSize, minSize);
        const offset  = (size - pxSize) / 2;
        return (
          <div
            className="game-player-highlight"
            style={{
              width       : size,
              height      : size,
              left        : pxLeft - offset,
              top         : pxTop  - offset,
              borderColor : invertHex(myPoint.color ?? '#ffffff'),
            }}
          />
        );
      })()}

      {/* "Go to" destination marker — harmless overlay until the walk is confirmed */}
      {gotoTarget && (() => {
        const pxLeft = (gotoTarget.x - viewport.x) * viewport.zoom + windowSize.width  / 2;
        const pxTop  = (viewport.y - gotoTarget.y) * viewport.zoom + windowSize.height / 2;
        const size   = Math.max(viewport.zoom, 14);
        const offset = (size - viewport.zoom) / 2;
        return (
          <div
            className={`game-goto-marker${gotoWalking ? ' game-goto-marker--walking' : ''}`}
            style={{ width: size, height: size, left: pxLeft - offset, top: pxTop - offset }}
          />
        );
      })()}

      {/* Coordinate grid overlay — toggled by button or keyboard G */}
      <GridOverlay
        viewport={viewport}
        width={windowSize.width}
        height={windowSize.height}
        visible={showGrid}
        brightness={gridBrightness}
      />

      {/* Pixel info — top-left on desktop, bottom-left on mobile */}
      <div className="game-hud-info game-hud-info--floating">
        {t('game.pointLabel', { id: pointId })}<br />
        X: <b>{myPoint?.x ?? '—'}</b> &nbsp; Y: <b>{myPoint?.y ?? '—'}</b><br />
        {t('game.colorLabel')}{' '}
        <b style={{ color: myPoint?.color ?? '#fff' }}>{myPoint?.color ?? '—'}</b><br />
        {maxPoints !== null && totalPoints !== null && (
          <span style={{ opacity: 0.75 }}>
            {t('game.pointsLabel')} <b style={{ color: totalPoints >= maxPoints ? '#ff8888' : '#fff' }}>
              {totalPoints}/{maxPoints}
            </b>
          </span>
        )}
      </div>

      {/* Top-right HUD — standard nav menu */}
      <div className="game-page__hud game-page__hud--top-right">
        <ConnectionDot statusInfo={wsStatus} />
        <AppNav currentPage="game" />
      </div>

      {/* Top-left HUD — quick action bar; "⋯" opens the full sheet on mobile */}
      <div className="game-page__hud game-page__hud--top-left">
        <div className="game-hud-actions">
          {hudActions.map(a => (
            <button
              key={a.key}
              className={`game-hud-icon-btn${a.quick ? ' is-quick' : ''}${a.active ? ' game-hud-icon-btn--active' : ''}${a.danger ? ' game-hud-icon-btn--danger' : ''}`}
              onClick={a.onClick}
              disabled={a.disabled}
              data-tip={a.label}
            >
              <span className="game-hud-icon-btn__icon" style={a.iconColor ? { color: a.iconColor } : undefined}>{a.icon}</span>
              <span className="game-hud-icon-btn__label">{a.label}</span>
              {a.accent && (
                <span className="game-hud-icon-btn__color-bar" style={{ background: a.accent }} />
              )}
            </button>
          ))}

          {/* Mobile only: open the full labeled action sheet */}
          <button
            className="game-hud-icon-btn hud-menu-toggle"
            onClick={() => setMenuOpen(true)}
            data-tip={t('game.moreActions')}
          >
            <span className="game-hud-icon-btn__icon">⋯</span>
            <span className="game-hud-icon-btn__label">{t('game.moreActions')}</span>
          </button>
        </div>
      </div>

      {/* Mobile action sheet — every HUD action, labeled, thumb-reachable */}
      {menuOpen && (
        <div className="game-action-sheet-overlay" onClick={() => setMenuOpen(false)}>
          <div className="game-action-sheet" onClick={e => e.stopPropagation()}>
            <div className="game-action-sheet__handle" />

            {/* Control layout switcher — one-hand / split / taps */}
            <div className="game-action-sheet__controls">
              <span className="game-action-sheet__controls-label">{t('game.controlsLayout')}</span>
              <div className="game-control-seg">
                {CONTROL_LAYOUTS.map(m => (
                  <button
                    key={m}
                    className={`game-control-seg__btn${controlLayout === m ? ' is-active' : ''}`}
                    onClick={() => selectControlLayout(m)}
                  >
                    {t(`game.controls_${m}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Hand — mirrors the controls to the left/right edge */}
            <div className="game-action-sheet__controls">
              <span className="game-action-sheet__controls-label">{t('game.controlsHand')}</span>
              <div className="game-control-seg">
                {CONTROL_HANDS.map(h => (
                  <button
                    key={h}
                    className={`game-control-seg__btn${controlHand === h ? ' is-active' : ''}`}
                    onClick={() => selectControlHand(h)}
                  >
                    {t(`game.hand_${h}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* D-pad size — irrelevant in taps mode (no d-pad) */}
            {controlLayout !== 'taps' && (
              <div className="game-action-sheet__controls">
                <span className="game-action-sheet__controls-label">{t('game.controlsSize')}</span>
                <div className="game-control-seg">
                  {DPAD_SIZES.map(s => (
                    <button
                      key={s}
                      className={`game-control-seg__btn${dpadSize === s ? ' is-active' : ''}`}
                      onClick={() => selectDpadSize(s)}
                    >
                      {t(`game.size_${s}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Grid brightness — helps the grid stay visible on dim phone screens */}
            <div className="game-action-sheet__controls">
              <span className="game-action-sheet__controls-label">{t('game.controlsGridBrightness')}</span>
              <div className="game-control-seg">
                {GRID_BRIGHTNESS_LEVELS.map(n => (
                  <button
                    key={n}
                    className={`game-control-seg__btn${gridBrightness === n ? ' is-active' : ''}`}
                    onClick={() => selectGridBrightness(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div className="game-action-sheet__grid">
              {hudActions.map(a => (
                <button
                  key={a.key}
                  className={`game-action-sheet__item${a.active ? ' is-active' : ''}${a.danger ? ' is-danger' : ''}`}
                  onClick={() => { setMenuOpen(false); a.onClick(); }}
                  disabled={a.disabled}
                >
                  <span className="game-action-sheet__icon" style={a.iconColor ? { color: a.iconColor } : undefined}>{a.icon}</span>
                  <span className="game-action-sheet__label">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pixel select overlay — pointer-events:none so canvas pan/drag still works */}
      {pixelSelectMode && (() => {
        const myUserId2 = myPoint?.user_id;
        const ownPoints = points.filter(p => p.user_id === myUserId2 && p.id !== Number(pointId));
        return (
          <div className="pixel-select-overlay" style={{ width: windowSize.width, height: windowSize.height }}>
            <div className="pixel-select-overlay__hint">
              {t('game.switchPixelHint')}
              <button
                className="pixel-select-overlay__close"
                onClick={() => setPixelSelectMode(false)}
              >✕</button>
            </div>
            {ownPoints.map(p => {
              const size = Math.max(viewport.zoom, 20);
              // pixel top-left on screen
              const sx = (p.x - viewport.x) * viewport.zoom + windowSize.width  / 2;
              const sy = (viewport.y - p.y)  * viewport.zoom + windowSize.height / 2;
              // center of pixel on screen
              const cx = sx + viewport.zoom / 2;
              const cy = sy + viewport.zoom / 2;
              return (
                <div
                  key={p.id}
                  className="pixel-select-highlight"
                  style={{
                    left       : cx - size / 2,
                    top        : cy - size / 2,
                    width      : size,
                    height     : size,
                    borderColor: p.color ?? '#fff',
                  }}
                  onClick={() => {
                    setPixelSelectMode(false);
                    navigate(`/game/${p.id}`, { state: { zoom: vpRef.current.zoom } });
                  }}
                  onTouchEnd={e => {
                    e.preventDefault();
                    setPixelSelectMode(false);
                    navigate(`/game/${p.id}`, { state: { zoom: vpRef.current.zoom } });
                  }}
                />
              );
            })}
          </div>
        );
      })()}

      {/* Zone management modal */}
      {showZoneModal && (
        <div className="pixel-modal-overlay" onClick={() => setShowZoneModal(false)}>
          <div className="pixel-modal" onClick={e => e.stopPropagation()}>
            <p className="pixel-modal__title">{t('zone.reserveTitle')}</p>
            <input
              className="pixel-modal__input"
              placeholder={t('zone.namePlaceholder')}
              value={zoneName}
              onChange={e => setZoneName(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', margin: '6px 0' }}>
              <input className="pixel-modal__input" placeholder="X min" value={zoneX1} onChange={e => setZoneX1(e.target.value)} />
              <input className="pixel-modal__input" placeholder="Y min" value={zoneY1} onChange={e => setZoneY1(e.target.value)} />
              <input className="pixel-modal__input" placeholder="X max" value={zoneX2} onChange={e => setZoneX2(e.target.value)} />
              <input className="pixel-modal__input" placeholder="Y max" value={zoneY2} onChange={e => setZoneY2(e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0' }}>
              <span style={{ fontSize: '11px', opacity: 0.7 }}>{t('zone.timeoutLabel')}</span>
              <input
                className="pixel-modal__input"
                style={{ width: '70px' }}
                type="number"
                min="5"
                value={zoneTimeout}
                onChange={e => setZoneTimeout(e.target.value)}
              />
              <span style={{ fontSize: '11px', opacity: 0.7 }}>{t('zone.seconds')}</span>
            </div>
            {/* Price preview */}
            <div style={{ fontSize: '12px', margin: '8px 0 4px', minHeight: '20px' }}>
              <span style={{ opacity: 0.6 }}>{t('zone.priceLabel')} </span>
              {zonePriceLoading
                ? <span style={{ opacity: 0.6 }}>{t('zone.priceCalculating')}</span>
                : zonePrice
                  ? zonePrice.is_free
                    ? <strong style={{ color: '#88ff88' }}>✓ {t('zone.priceFree')}</strong>
                    : <strong style={{ color: '#ffcc44' }}>{t('zone.priceAmount', { amount: zonePrice.amount_rub })}</strong>
                  : null}
            </div>
            <div style={{ fontSize: '10px', opacity: 0.45, marginBottom: '4px' }}>{t('zone.freeHint')}</div>
            {zonePrice?.is_free && zoneFreeUsed >= 1 && (
              <div style={{ fontSize: '11px', color: '#ff8888', marginBottom: '6px' }}>
                {t('myZones.freeQuotaUsed')}
              </div>
            )}

            <p style={{ fontSize: '10px', opacity: 0.55, margin: '4px 0 6px', textAlign: 'center' }}>
              {t('common.ofertaConsent')}{' '}
              <Link to="/about?tab=oferta" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--px-accent)', textDecoration: 'underline' }}>
                {t('common.ofertaLink')}
              </Link>
            </p>

            <div className="pixel-modal__actions">
              <button className="pixel-modal__btn" onClick={() => setShowZoneModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="pixel-modal__btn"
                onClick={() => { setShowZoneModal(false); setZoneDrawMode(true); }}
              >
                {t('zone.drawBtn')}
              </button>
              <button
                className="pixel-modal__btn pixel-modal__btn--primary"
                onClick={handleCreateZone}
                disabled={creatingZone || (zonePrice?.is_free === true && zoneFreeUsed >= 1)}
              >
                {creatingZone
                  ? t('common.saving')
                  : zonePrice && !zonePrice.is_free
                    ? t('myZones.payBtn', { amount: zonePrice.amount_rub })
                    : t('zone.createBtn')}
              </button>
            </div>

            {/* List of own zones in the current viewport area */}
            {zones.filter(z => z.user_id === myUserId).length > 0 && (
              <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '10px' }}>
                <p style={{ fontSize: '10px', opacity: 0.6, marginBottom: '6px' }}>{t('zone.myZones')}</p>
                {zones.filter(z => z.user_id === myUserId).map(z => {
                  const active = isZoneActive(z);
                  const expiryStr = z.expires_at
                    ? t('zone.expiresAt', { date: new Date(z.expires_at).toLocaleDateString() })
                    : z.is_free ? null : t('zone.pendingPayment');
                  return (
                    <div
                      key={z.id}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', fontSize: '11px', opacity: active ? 1 : 0.55 }}
                    >
                      <span>
                        {z.name || `#${z.id}`} ({z.x_min},{z.y_min})–({z.x_max},{z.y_max}) · {z.eviction_timeout_sec}с
                        {expiryStr && <span style={{ marginLeft: '4px', opacity: 0.7 }}>· {expiryStr}</span>}
                      </span>
                      <button
                        className="pixel-modal__btn"
                        style={{ padding: '2px 8px', fontSize: '10px', marginLeft: '8px' }}
                        onClick={() => setZoneDeleteTarget(z)}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Zone delete confirmation modal */}
      {zoneDeleteTarget && (
        <div className="pixel-modal-overlay" onClick={() => !zoneDeletingId && setZoneDeleteTarget(null)}>
          <div className="pixel-modal" style={{ maxWidth: '340px' }} onClick={e => e.stopPropagation()}>
            <p className="pixel-modal__title">{t('zone.deleteConfirmTitle')}</p>
            <p style={{ fontSize: '13px', marginBottom: '1rem', opacity: 0.85 }}>
              {t('zone.deleteConfirmText', { name: zoneDeleteTarget.name || `#${zoneDeleteTarget.id}` })}
            </p>
            {zoneDeleteTarget.expires_at && !zoneDeleteTarget.is_free && (
              <p style={{ fontSize: '11px', color: '#ffaa44', marginBottom: '0.75rem' }}>
                ⚠ {t('zone.deleteNoRefund')}
              </p>
            )}
            <div className="pixel-modal__actions">
              <button className="pixel-modal__btn" onClick={() => setZoneDeleteTarget(null)} disabled={!!zoneDeletingId}>
                {t('common.cancel')}
              </button>
              <button
                className="pixel-modal__btn pixel-modal__btn--primary"
                style={{ background: 'rgba(200,40,40,0.7)' }}
                onClick={handleDeleteZone}
                disabled={!!zoneDeletingId}
              >
                {zoneDeletingId ? '...' : t('zone.deleteConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zone draw mode overlay */}
      {zoneDrawMode && (
        <ZoneDrawOverlay
          viewport={viewport}
          width={windowSize.width}
          height={windowSize.height}
          onSelect={(x1, y1, x2, y2) => {
            setZoneX1(String(x1));
            setZoneY1(String(y1));
            setZoneX2(String(x2));
            setZoneY2(String(y2));
            setZoneDrawMode(false);
            setShowZoneModal(true);
          }}
          onCancel={() => { setZoneDrawMode(false); setShowZoneModal(true); }}
        />
      )}

      {/* Say input overlay — appears at bottom-center when Say button clicked */}
      {showSayInput && (
        <div className="say-input-overlay">
          <select
            className="say-style-select"
            value={sayStyle}
            onChange={e => setSayStyle(e.target.value as SpeechBubbleStyle)}
            disabled={!subscriptionActive}
            title={subscriptionActive ? t('speech.styleLabel') : t('speech.styleLockedHint')}
          >
            {SPEECH_STYLE_OPTIONS.map(option => (
              <option
                key={option.id}
                value={option.id}
                disabled={!subscriptionActive && isPremiumSpeechStyle(option.id)}
              >
                {t(option.labelKey)}
              </option>
            ))}
          </select>
          <input
            className="say-input"
            placeholder={t('game.sayPlaceholder')}
            value={sayText}
            maxLength={200}
            autoFocus
            onChange={e => setSayText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); handleSaySubmit(sayText, jumpOnSay, sayStyle); }
              if (e.key === 'Escape') { e.preventDefault(); setShowSayInput(false); }
            }}
          />
          <label className={`say-jump-toggle${jumpOnSay ? ' say-jump-toggle--active' : ''}`} title={t('game.sayJump')}>
            <input
              type="checkbox"
              checked={jumpOnSay}
              onChange={e => setJumpOnSay(e.target.checked)}
            />
            <span>↑</span>
          </label>
          <button className="say-send-btn" onClick={() => handleSaySubmit(sayText, jumpOnSay, sayStyle)}>▶</button>
        </div>
      )}

      {/* "Go to" control bar — tap a cell, then GO to auto-walk there */}
      {gotoMode && (
        <div className="game-goto-bar">
          {!gotoTarget ? (
            <span className="game-goto-bar__hint">{t('game.gotoHint')}</span>
          ) : gotoWalking ? (
            <>
              <span className="game-goto-bar__hint">{t('game.gotoWalking')}</span>
              <button className="game-goto-bar__btn game-goto-bar__btn--stop" onClick={cancelGoto}>{t('game.gotoStop')}</button>
            </>
          ) : (
            <>
              <span className="game-goto-bar__hint">{gotoTarget.x}, {gotoTarget.y}</span>
              <button className="game-goto-bar__btn game-goto-bar__btn--go" onClick={startGotoWalk}>▶ {t('game.gotoGo')}</button>
              <button className="game-goto-bar__btn" onClick={cancelGoto}>✕</button>
            </>
          )}
        </div>
      )}

      {/* Speech bubbles — comic balloons above pixels */}
      <SpeechBubbles
        speeches={speeches}
        points={points}
        viewport={viewport}
        windowSize={windowSize}
      />

      {/* Color picker modal */}
      <ColorPickerModal
        open={colorOpen}
        title={t('game.chooseColorTitle')}
        currentColor={myPoint?.color ?? '#FFFFFF'}
        onClose={() => setColorOpen(false)}
        onApply={submitColor}
      />

      {/* Bottom-right HUD */}
      <div className="game-page__hud game-page__hud--bottom-right">
        <div className="game-hud-dpad">
          <button className="btn-transparent dpad-up"    {...holdUp}>↑</button>
          <button className="btn-transparent dpad-left"  {...holdLeft}>←</button>
          <button className="btn-transparent dpad-down"  {...holdDown}>↓</button>
          <button className="btn-transparent dpad-right" {...holdRight}>→</button>
        </div>
        <div className="game-hud-zoom">
          <button className="btn-transparent" onClick={() => zoomBy(1.3)}>+</button>
          <button className="btn-transparent" onClick={() => zoomBy(1 / 1.3)}>−</button>
        </div>
        <button
          className={`btn-transparent game-hud-grid-btn${showGrid ? ' game-hud-grid-btn--active' : ''}`}
          onClick={() => setShowGrid(v => { const next = !v; localStorage.setItem('showGrid', String(next)); return next; })}
          title="Toggle coordinate grid (G)"
        >
          {showGrid ? '▨ ' : '▦ '}{showGrid ? t('game.gridOff') : t('game.gridOn')}
        </button>
      </div>

      {/* Tap-zones affordance — only in the `taps` preset. Faint edge arrows
          mark the tappable regions; pointer-events:none so taps reach the canvas. */}
      {controlLayout === 'taps' && (
        <div className="game-tap-zones" aria-hidden="true">
          <span className="game-tap-zones__arrow game-tap-zones__arrow--up">↑</span>
          <span className="game-tap-zones__arrow game-tap-zones__arrow--down">↓</span>
          <span className="game-tap-zones__arrow game-tap-zones__arrow--left">←</span>
          <span className="game-tap-zones__arrow game-tap-zones__arrow--right">→</span>
        </div>
      )}


      {/* Admin perf overlay — bottom-left, visible only for admins */}
      {isAdmin && (
        <div style={{
          position: 'fixed', bottom: '12px', left: '12px',
          fontFamily: 'monospace', fontSize: '11px', color: '#fff',
          background: 'rgba(0,0,0,0.55)', padding: '4px 8px', borderRadius: '4px',
          pointerEvents: 'none', zIndex: 900, lineHeight: 1.6,
        }}>
          <div ref={fpsDivRef}>FPS: —</div>
          <div ref={rttDivRef}>RTT: —</div>
          <div ref={snapDivRef} style={{ maxWidth: '640px', whiteSpace: 'normal' }}>Snap: —</div>
        </div>
      )}

      {/* Help / controls modal */}
      {showHelp && (
        <div className="pixel-modal-overlay" onClick={() => setShowHelp(false)}>
          <div className="pixel-modal pixel-modal--help" onClick={e => e.stopPropagation()}
            style={{ maxHeight: '85vh', overflowY: 'auto', maxWidth: 480 }}>
            <p className="pixel-modal__title">{t('game.helpTitle')}</p>

            {/* Keyboard controls */}
            <p className="game-help-section-title">{t('game.helpSectionControls')}</p>
            <table className="game-help-table">
              <tbody>
                <tr><td>WASD / ←↑↓→</td><td>{t('game.helpMove')}</td></tr>
                <tr><td>C</td><td>{t('game.helpColor')}</td></tr>
                <tr><td>G</td><td>{t('game.helpGrid')}</td></tr>
                <tr><td>T</td><td>{t('game.helpSay')}</td></tr>
                <tr><td>Shift+T</td><td>{t('game.helpSayJump')}</td></tr>
                <tr><td>J</td><td>{t('game.helpJump')}</td></tr>
                <tr><td>Space</td><td>{t('game.helpCreate')}</td></tr>
                <tr><td>Delete</td><td>{t('game.helpDelete')}</td></tr>
              </tbody>
            </table>

            {/* HUD buttons */}
            <p className="game-help-section-title" style={{ marginTop: '1rem' }}>{t('game.helpSectionHud')}</p>
            <ul className="game-help-list">
              <li>{t('game.helpHudDashboard')}</li>
              <li>{t('game.helpHudCanvas')}</li>
              <li>{t('game.helpHudColor')}</li>
              <li>{t('game.helpHudPoint')}</li>
              <li>{t('game.helpHudSwitch')}</li>
              <li>{t('game.helpHudGoto')}</li>
              <li>{t('game.helpHudZone')}</li>
              <li>{t('game.helpHudGrid')}</li>
              <li>{t('game.helpHudShare')}</li>
              <li>{t('game.helpHudSay')}</li>
              <li>{t('game.helpHudWall')}</li>
              <li>{t('game.helpHudDelete')}</li>
            </ul>

            {/* Mechanics */}
            <p className="game-help-section-title" style={{ marginTop: '1rem' }}>{t('game.helpSectionMechanics')}</p>
            <ul className="game-help-list">
              <li>{t('game.helpMechMove')}</li>
              <li>{t('game.helpMechRate')}</li>
              <li>{t('game.helpMechSync')}</li>
              <li>{t('game.helpMechCreate')}</li>
            </ul>

            {/* Colors */}
            <p className="game-help-section-title" style={{ marginTop: '1rem' }}>{t('game.helpSectionColors')}</p>
            <ul className="game-help-list">
              <li>{t('game.helpColorFree')}</li>
              <li>{t('game.helpColorPremium')}</li>
              <li>{t('game.helpColorCustom')}</li>
            </ul>

            {/* Zones */}
            <p className="game-help-section-title" style={{ marginTop: '1rem' }}>{t('game.helpSectionZones')}</p>
            <ul className="game-help-list">
              <li>{t('game.helpZoneDesc')}</li>
              <li>{t('game.helpZoneWarning')}</li>
              <li>{t('game.helpZoneFree')}</li>
              <li>{t('game.helpZoneWall')}</li>
            </ul>

            <div className="pixel-modal__actions" style={{ marginTop: '1.25rem' }}>
              <button className="pixel-modal__btn pixel-modal__btn--primary" onClick={() => setShowHelp(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
